interface AdminTokenData {
  token: string;
  expiryTime: number;
  issued: number;
}

class AdminSecureStorage {
  private static readonly TOKEN_KEY = 'admin_vivaclub_token';
  private static readonly TOKEN_EXPIRY_BUFFER_HOURS = 1; // Consider token expired 1 hour before actual expiry
  
  /**
   * Store admin token with expiry information
   */
  static setAdminToken(token: string, expiryHours: number = 24): void {
    try {
      const now = Date.now();
      const expiryTime = now + (expiryHours * 60 * 60 * 1000);
      
      const tokenData: AdminTokenData = {
        token,
        expiryTime,
        issued: now
      };
      
      localStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokenData));
    } catch (error) {
      console.error('Failed to store admin token:', error);
    }
  }
  
  /**
   * Get admin token if valid
   */
  static getAdminToken(): string | null {
    try {
      const stored = localStorage.getItem(this.TOKEN_KEY);
      if (!stored) return null;
      
      const tokenData: AdminTokenData = JSON.parse(stored);
      const now = Date.now();
      const bufferTime = this.TOKEN_EXPIRY_BUFFER_HOURS * 60 * 60 * 1000;
      
      // Check if token is expired (with buffer)
      if (now >= (tokenData.expiryTime - bufferTime)) {
        this.removeAdminToken();
        return null;
      }
      
      return tokenData.token;
    } catch (error) {
      console.error('Failed to retrieve admin token:', error);
      this.removeAdminToken();
      return null;
    }
  }
  
  /**
   * Check if admin token is valid
   */
  static isAdminTokenValid(): boolean {
    return this.getAdminToken() !== null;
  }
  
  /**
   * Get token expiry time
   */
  static getTokenExpiryTime(): number | null {
    try {
      const stored = localStorage.getItem(this.TOKEN_KEY);
      if (!stored) return null;
      
      const tokenData: AdminTokenData = JSON.parse(stored);
      return tokenData.expiryTime;
    } catch (error) {
      console.error('Failed to get token expiry time:', error);
      return null;
    }
  }
  
  /**
   * Remove admin token
   */
  static removeAdminToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Failed to remove admin token:', error);
    }
  }
  
  /**
   * Clear all admin data
   */
  static clearAll(): void {
    this.removeAdminToken();
  }
}

export default AdminSecureStorage;
